import {
  Base64FromString,
  GetRouteMatchType,
  GetSetRouteMatchType,
  IntFromString,
  LenientBooleanFromString,
  route,
  RouteMap
} from '@st/util/routing'
import type { OAuthProvider } from '@st/sdk'
import type { TaxAppId } from '@st/tax-folder'
import { Schema, SchemaTransformation } from 'effect'
import { match } from '@st/pattern'
import { clientAppBaseUrl } from '~/env'
import { upgradeLegacyId } from '~/util/legacy-id'
import { ClientPacketsFlowIdFromString } from '~/features/client-packets/client-packets-flow'

export type { ClientPacketsFlowId } from '~/features/client-packets/client-packets-flow'

const SchemaIdFromString = Schema.String.pipe(
  Schema.decodeTo(
    Schema.String,
    SchemaTransformation.transform({
      decode: (value) => value.toLowerCase(),
      encode: (value) => value.toLowerCase()
    })
  )
)

export const MAGIC_LINK_PORTALS = [
  'karbon',
  'canopy',
  'tax_dome',
  'financial_cents',
  'firm360',
  'qount',
  'double',
  'anchor',
  'other'
] as const
export type MagicLinkPortal = (typeof MAGIC_LINK_PORTALS)[number]

const LegacyIdFromString = Schema.String.check(
  Schema.makeFilter((value) => upgradeLegacyId(value) !== value)
)

export const SignUpFlowIdFromString = Schema.Literals([
  'client-packets',
  'personalized-checklists',
  'personalized-pdf'
])
export type SignUpFlowId = typeof SignUpFlowIdFromString.Type
const NewClientMethodFromString = Schema.Literals(['bulk-csv', 'one-by-one'])

const taxAppIds = [
  'csv',
  'stanfordtax',
  'drake',
  'ultratax',
  'lacerte',
  'atx',
  'proconnect',
  'proseries',
  'prosystem',
  'axcess',
  'taxact',
  'taxslayer',
  'taxwise',
  'gosystem_tax_rs',
  'turbotax',
  'crosslink'
] as const satisfies readonly TaxAppId[]
const TaxAppIdFromString = Schema.Literals(taxAppIds)

const OrganizationSlugFields = {
  organizationSlug: Schema.String
}

const ImportBaseFields = {
  ...OrganizationSlugFields,
  preview: Schema.optional(LenientBooleanFromString)
}

export const rootRoute = route('/', '/', Schema.Struct({ code: Schema.optional(Schema.String) }))

export const organizationFolderLegacyRoute = route(
  'organization_folder_legacy',
  '/:organizationSlug/binders/:folderId',
  Schema.Struct({
    ...OrganizationSlugFields,
    folderId: LegacyIdFromString
  })
)

export const organizationWorkpaperLegacyRoute = route(
  'organization_workpaper_legacy',
  '/:organizationSlug/workpapers/:folderId',
  Schema.Struct({
    ...OrganizationSlugFields,
    folderId: LegacyIdFromString
  })
)

export const organizationFolderRoute = route(
  'organization_folder',
  '/:organizationSlug/binders/:folderId',
  Schema.Struct({
    ...OrganizationSlugFields,
    folderId: Schema.String
  })
)

export const organizationFolderDebug = route(
  'organization_folder_debug',
  '/:organizationSlug/binders/:folderId/debug',
  Schema.Struct({
    ...OrganizationSlugFields,
    folderId: Schema.String
  })
)

export const organizationPreviewQuestionnaire = route(
  'organization_preview_questionnaire',
  '/:organizationSlug/binders/:folderId/preview-organizer',
  Schema.Struct({
    ...OrganizationSlugFields,
    folderId: Schema.String
  })
)

export const clientFolderLegacyRoute = route(
  'client_folder_legacy',
  '/:organizationSlug/folders/:folderId',
  Schema.Struct({
    ...OrganizationSlugFields,
    folderId: LegacyIdFromString
  })
)

export const clientFolderRoute = route(
  'client_folder',
  '/:organizationSlug/folders/:folderId',
  Schema.Struct({
    ...OrganizationSlugFields,
    folderId: Schema.String,
    aud: Schema.optional(Schema.String)
  })
)

export const clientFolderLoggedOutRoute = route(
  'client_folder_logged_out',
  '/:organizationSlug/folders/:folderId/logged-out',
  Schema.Struct({
    ...OrganizationSlugFields,
    folderId: Schema.String
  })
)

export const oauthCallbackRoute = route(
  'oauth_callback',
  '/oauth/callback/:provider',
  Schema.Struct({
    provider: Schema.String,
    code: Schema.optional(Schema.String),
    state: Schema.optional(Schema.String)
  })
)

export const submissionListLegacyRoute = route(
  'submission_list_legacy',
  '/:organizationSlug/submissions',
  Schema.Struct(OrganizationSlugFields)
)

export const workpaperListLegacyRoute = route(
  'workpaper_list_legacy',
  '/:organizationSlug/workpapers',
  Schema.Struct(OrganizationSlugFields)
)

export const folderListRoute = route(
  'folder_list',
  '/:organizationSlug/binders',
  Schema.Struct(OrganizationSlugFields)
)

export const magicLinksRoute = route(
  'magic_links',
  '/:organizationSlug/magic-links',
  Schema.Struct(OrganizationSlugFields)
)

export const folderListSendClientOrganizersRoute = route(
  'folder_list_send_client_organizers',
  '/:organizationSlug/binders/send-organizers',
  Schema.Struct(OrganizationSlugFields)
)

export const folderListOrganizerRemindersRoute = route(
  'folder_list_organizer_reminders',
  '/:organizationSlug/binders/organizer-reminders',
  Schema.Struct(OrganizationSlugFields)
)

export const folderListDownloadQuestionnaireLinksRoute = route(
  'folder_list_download_questionnaire_links',
  '/:organizationSlug/binders/download-client-organizer-links',
  Schema.Struct(OrganizationSlugFields)
)

export const connectAppRoute = route(
  'connect_app',
  '/connect/:app_name',
  Schema.Struct({
    app_name: Schema.String,
    // The Connect feature owns its missing-parameter error screen, so these
    // remain optional at the routing boundary despite being required for a
    // valid authorization request.
    client_id: Schema.optional(Schema.String),
    state: Schema.optional(Schema.String),
    redirect_uri: Schema.optional(Schema.String),
    scope: Schema.optional(Schema.String),
    code_challenge: Schema.optional(Schema.String),
    code_challenge_method: Schema.optional(Schema.String)
  })
)

export const createImportRoute = route(
  'create_import',
  '/:organizationSlug/import',
  Schema.Struct(ImportBaseFields)
)

export const createImportNewClientRoute = route(
  'create_import_new_client',
  '/:organizationSlug/import',
  Schema.Struct({
    ...ImportBaseFields,
    'client-kind': Schema.Literal('new')
  })
)

export const createImportNewClientMethodRoute = route(
  'create_import_new_client_method',
  '/:organizationSlug/import',
  Schema.Struct({
    ...ImportBaseFields,
    'client-kind': Schema.Literal('new'),
    'new-client-method': NewClientMethodFromString
  })
)

export const createImportReturningClientRoute = route(
  'create_import_returning_client',
  '/:organizationSlug/import',
  Schema.Struct({
    ...ImportBaseFields,
    'client-kind': Schema.Literal('returning')
  })
)

export const createImportTaxAppRoute = route(
  'create_import_tax_app',
  '/:organizationSlug/import',
  Schema.Struct({
    ...ImportBaseFields,
    'client-kind': Schema.Literal('returning'),
    'tax-app': TaxAppIdFromString
  })
)

export const createImportTaxYearRoute = route(
  'create_import_tax_year',
  '/:organizationSlug/import',
  Schema.Struct({
    ...ImportBaseFields,
    'client-kind': Schema.Literal('returning'),
    'tax-app': TaxAppIdFromString,
    'tax-year': IntFromString
  })
)

export const runImportRoute = route(
  'run_import',
  '/:organizationSlug/import/:importId',
  Schema.Struct({
    ...OrganizationSlugFields,
    importId: Schema.String
  })
)

export const clientPacketsUploadBackupRoute = route(
  'client_packets_upload_backup',
  '/:organizationSlug/client-packets/upload-backup',
  Schema.Struct({
    ...OrganizationSlugFields,
    'flow': Schema.optional(ClientPacketsFlowIdFromString),
    'tax-app': Schema.optional(Schema.String)
  })
)

export const clientPacketsExportRoute = route(
  'client_packets_export',
  '/:organizationSlug/client-packets/:importId/export-packets',
  Schema.Struct({
    ...OrganizationSlugFields,
    importId: Schema.String,
    flow: Schema.optional(ClientPacketsFlowIdFromString)
  })
)

export const manageAccountRoute = route(
  'account',
  '/:organizationSlug/settings/account',
  Schema.Struct(OrganizationSlugFields)
)

export const manageOrganizationTagsRoute = route(
  'manage_organization_tags',
  '/:organizationSlug/settings/tags',
  Schema.Struct(OrganizationSlugFields)
)

export const clientOrganizerSettingsRoute = route(
  'client_organizer_settings',
  '/:organizationSlug/settings/client-organizer',
  Schema.Struct(OrganizationSlugFields)
)

export const manageOrganizationIntegrationsRoute = route(
  'manage_organization_integrations',
  '/:organizationSlug/settings/integrations',
  Schema.Struct(OrganizationSlugFields)
)

export const desktopAgentSettingsRoute = route(
  'desktop_agent_settings',
  '/:organizationSlug/settings/desktop-agent',
  Schema.Struct(OrganizationSlugFields)
)

export const manageOrganizationMessageTemplatesRoute = route(
  'manage_organization_message_templates',
  '/:organizationSlug/settings/templates',
  Schema.Struct(OrganizationSlugFields)
)

export const organizationBalanceRoute = route(
  'organization_balance',
  '/:organizationSlug/settings/balance',
  Schema.Struct(OrganizationSlugFields)
)

export const brandingRoute = route(
  'branding',
  '/:organizationSlug/settings/branding',
  Schema.Struct(OrganizationSlugFields)
)

export const teamRoute = route(
  'team',
  '/:organizationSlug/settings/team',
  Schema.Struct(OrganizationSlugFields)
)

export const teamInvitation = route(
  'team_invitation',
  '/:organizationSlug/invitation/:email',
  Schema.Struct({
    ...OrganizationSlugFields,
    email: Base64FromString
  })
)

export const loggedInPricing = route(
  'logged_in_pricing',
  '/:organizationSlug/pricing',
  Schema.Struct(OrganizationSlugFields)
)

export const oneHourCourseRoute = route('one_hour_course', '/academy/one-hour-course')

export const oneHourCourseLegacyRoute = route(
  'one_hour_course_legacy',
  '/:organizationSlug/resources/one-hour-course',
  Schema.Struct(OrganizationSlugFields)
)

export const loggedOutPricing = route('logged_out_pricing', '/pricing')

export const updatesRoute = route('updates', '/updates')

export const loginRoute = route('login', '/login')

export const signUpRoute = route(
  'sign_up',
  '/sign-up',
  Schema.Struct({
    email: Schema.optional(Schema.String),
    organizationName: Schema.optional(Schema.String),
    phoneNumber: Schema.optional(Schema.String),
    trial: Schema.optional(LenientBooleanFromString),
    flow: Schema.optional(SignUpFlowIdFromString)
  })
)

const ClientQuestionnaireFields = {
  email: Schema.optional(Schema.String),
  ...OrganizationSlugFields,
  portal: Schema.optional(Schema.String),
  schemaId: SchemaIdFromString
}

export const clientQuestionnaireRoute = route(
  'client_questionnaire',
  '/:organizationSlug/questionnaire/:schemaId/:clientKind',
  Schema.Struct({
    ...ClientQuestionnaireFields,
    clientKind: Schema.Literals(['new', 'returning'])
  })
)

export const clientQuestionnaireLegacyRoute = route(
  'client_questionnaire_legacy',
  '/:organizationSlug/questionnaire/:schemaId',
  Schema.Struct(ClientQuestionnaireFields)
)

export const securityRoute = route('security', '/security')

export const paymentGuideRoute = route(
  'payment_guide',
  '/payment-instructions/:returnType/:taxYear/:jurisdiction/:paymentPurpose/:method',
  Schema.Struct({
    returnType: Schema.String,
    taxYear: IntFromString,
    jurisdiction: Schema.String,
    paymentPurpose: Schema.Literals(['balance_due', 'estimated_tax']),
    method: Schema.Literals(['online', 'mail'])
  })
)

const routesMap = {
  paymentGuideRoute,
  rootRoute,
  loginRoute,
  oauthCallbackRoute,
  signUpRoute,
  connectAppRoute,
  loggedOutPricing,
  securityRoute,
  organizationPreviewQuestionnaire,
  organizationFolderDebug,
  organizationFolderLegacyRoute,
  organizationWorkpaperLegacyRoute,
  folderListRoute,
  magicLinksRoute,
  folderListSendClientOrganizersRoute,
  folderListOrganizerRemindersRoute,
  folderListDownloadQuestionnaireLinksRoute,
  organizationFolderRoute,
  workpaperListLegacyRoute,
  submissionListLegacyRoute,
  manageAccountRoute,
  manageOrganizationTagsRoute,
  manageOrganizationMessageTemplatesRoute,
  clientOrganizerSettingsRoute,
  organizationBalanceRoute,
  manageOrganizationIntegrationsRoute,
  desktopAgentSettingsRoute,
  brandingRoute,
  teamRoute,
  teamInvitation,
  loggedInPricing,
  oneHourCourseRoute,
  oneHourCourseLegacyRoute,
  clientPacketsUploadBackupRoute,
  clientPacketsExportRoute,
  runImportRoute,
  // RouteMap takes the first schema-valid match, so shared import paths stay
  // ordered from the most complete wizard state to its nearest fallback.
  createImportTaxYearRoute,
  createImportNewClientMethodRoute,
  createImportTaxAppRoute,
  createImportNewClientRoute,
  createImportReturningClientRoute,
  createImportRoute,
  clientQuestionnaireRoute,
  clientQuestionnaireLegacyRoute,
  clientFolderLegacyRoute,
  clientFolderLoggedOutRoute,
  clientFolderRoute
}
export const routes = new RouteMap(routesMap)

export type AppRoute = GetRouteMatchType<typeof routesMap>
export type SetAppRoute = GetSetRouteMatchType<typeof routesMap>

function assertRoutePath(name: string, path: string | undefined): string {
  if (!path) {
    throw new Error(`Invalid path for route: ${name}`)
  }
  return path
}

export function toPath(route: AppRoute): string {
  return assertRoutePath(route.name, routes.toPath(route))
}

export function toPathFrom(currentRoute: AppRoute, route: SetAppRoute): string {
  return assertRoutePath(route.name, routes.toPath(route, currentRoute))
}

type ClientFolderShareableLink = {
  organizationSlug: string
  folderId: string
  /** The OAuth provider the client signs in through. */
  aud?: OAuthProvider
}
export function clientFolderShareableLink(link: ClientFolderShareableLink): string {
  return (
    clientAppBaseUrl() +
    clientFolderRoute({
      organizationSlug: link.organizationSlug,
      folderId: link.folderId,
      aud: link.aud
    })
  )
}

type MagicLink = {
  organizationSlug: string
  schemaId: string
  clientKind: 'new' | 'returning'
  portal?: MagicLinkPortal
}
export function clientMagicLink(link: MagicLink): string {
  return (
    clientAppBaseUrl() +
    clientQuestionnaireRoute({
      portal: magicLinkPortalSlug(link.portal),
      clientKind: link.clientKind,
      organizationSlug: link.organizationSlug,
      schemaId: link.schemaId
    })
  )
}

function magicLinkPortalSlug(portal: MagicLinkPortal | undefined): string | undefined {
  if (portal === undefined) return undefined

  return match(portal)
    .returnType<string>()
    .with('tax_dome', () => 'tax-dome')
    .with('financial_cents', () => 'financial-cents')
    .otherwise((portal) => portal)
}
